import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { chemistryApi } from '../../api/chemistry';
import StructureViewer from '../../components/chemistry/StructureViewer';
import ProteinLigandCard, { ProteinLigand } from '../../components/chemistry/ProteinLigandCard';
import { getStoredToken } from '../../utils/tokenStorage';

const getAuthHeaders = (): HeadersInit => {
  const token = getStoredToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

interface Biologic {
  id: number;
  file_id: number | null;
  name: string | null;
  sequence: string;
  sequence_type: string | null;
  organism: string | null;
  uniprot_id: string | null;
  molecular_weight: number | null;
  properties: Record<string, any>;
}

interface LinkedReaction {
  id: number;
  reaction_id: number;
  role: string;
  reaction_smiles: string | null;
  file_id: number;
  file_name: string | null;
}

interface LinkedLigand extends ProteinLigand {
  id: number;
  molecule_id: number;
  interaction_type: string;
  affinity_value: number | null;
  affinity_unit: string | null;
  smiles: string | null;
  file_id: number | null;
}

interface PathwayInfo {
  id: string;
  name: string;
}

interface InteractorInfo {
  name: string;
  score: number;
}

interface EnrichmentData {
  uniprot_id: string;
  protein_name: string | null;
  ensembl_id: string | null;
  pathway_count: number;
  interaction_count: number;
  top_pathways: PathwayInfo[];
  top_interactors: InteractorInfo[];
}

const ProteinDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [protein, setProtein] = useState<Biologic | null>(null);
  const [reactions, setReactions] = useState<LinkedReaction[]>([]);
  const [ligands, setLigands] = useState<LinkedLigand[]>([]);
  const [enrichment, setEnrichment] = useState<EnrichmentData | null>(null);
  const [enrichmentLoading, setEnrichmentLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [structureViewerOpen, setStructureViewerOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'pathways' | 'interactions' | 'reactions' | 'ligands'>('overview');

  const loadProteinData = useCallback(async () => {
    if (!id) return;

    setLoading(true);
    setError(null);

    try {
      const proteinId = parseInt(id, 10);

      // Load protein, reactions, and ligands in parallel
      const [proteinRes, reactionsRes, ligandsRes] = await Promise.all([
        chemistryApi.getBiologic(proteinId),
        chemistryApi.getBiologicReactions(proteinId),
        chemistryApi.getBiologicLigands(proteinId),
      ]);

      setProtein(proteinRes);
      setReactions(reactionsRes.reactions || []);
      setLigands(ligandsRes.ligands || []);

      // If protein has UniProt ID, fetch enrichment data
      if (proteinRes.uniprot_id) {
        setEnrichmentLoading(true);
        try {
          const enrichRes = await fetch(`/api/enrich/protein/${proteinRes.uniprot_id}`, {
            headers: getAuthHeaders(),
          });
          if (enrichRes.ok) {
            const data = await enrichRes.json();
            setEnrichment(data);
          }
        } catch (err) {
          console.warn('Failed to load enrichment data:', err);
        } finally {
          setEnrichmentLoading(false);
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load protein data');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadProteinData();
  }, [loadProteinData]);

  const formatMW = (mw: number | null) => {
    if (!mw) return '-';
    return mw.toLocaleString(undefined, { maximumFractionDigits: 1 }) + ' Da';
  };

  const getTypeColor = (type: string | null) => {
    const colors: Record<string, string> = {
      protein: 'bg-blue-100 text-blue-800 border-blue-200',
      enzyme: 'bg-green-100 text-green-800 border-green-200',
      peptide: 'bg-purple-100 text-purple-800 border-purple-200',
      antibody: 'bg-orange-100 text-orange-800 border-orange-200',
    };
    return colors[type || ''] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getScoreColor = (score: number) => {
    if (score >= 900) return 'bg-green-500';
    if (score >= 700) return 'bg-blue-500';
    if (score >= 400) return 'bg-yellow-500';
    return 'bg-gray-400';
  };

  const getScoreLabel = (score: number) => {
    if (score >= 900) return 'highest';
    if (score >= 700) return 'high';
    if (score >= 400) return 'medium';
    return 'low';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  if (error || !protein) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-red-500 mb-4">{error || 'Protein not found'}</p>
          <button
            onClick={() => navigate('/omics')}
            className="px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700"
          >
            Back to Biologics
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b px-6 py-4">
        <div className="flex items-center gap-4 mb-2">
          <button
            onClick={() => navigate('/omics')}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getTypeColor(protein.sequence_type)}`}>
            {protein.sequence_type || 'protein'}
          </span>
          <h1 className="text-2xl font-bold text-gray-900">{protein.name || `Protein #${protein.id}`}</h1>
          {enrichment?.protein_name && enrichment.protein_name !== protein.name && (
            <span className="text-gray-500 text-lg">({enrichment.protein_name})</span>
          )}
          {protein.uniprot_id && (
            <a
              href={`https://www.uniprot.org/uniprotkb/${protein.uniprot_id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-blue-50 border border-blue-200 text-blue-700 hover:bg-blue-100 hover:border-blue-300 transition-colors font-mono text-sm font-semibold"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
              {protein.uniprot_id}
              <svg className="w-3 h-3 opacity-60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          )}
        </div>

        {/* Quick stats */}
        <div className="flex items-center gap-6 text-sm text-gray-600">
          {protein.organism && (
            <span className="flex items-center gap-1">
              <span className="text-gray-400">Organism:</span>
              <span className="font-medium">{protein.organism}</span>
            </span>
          )}
          <span className="flex items-center gap-1">
            <span className="text-gray-400">Length:</span>
            <span className="font-medium">{protein.sequence.length} aa</span>
          </span>
          <span className="flex items-center gap-1">
            <span className="text-gray-400">MW:</span>
            <span className="font-medium">{formatMW(protein.molecular_weight)}</span>
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b px-6">
        <div className="flex gap-6">
          <button
            onClick={() => setActiveTab('overview')}
            className={`py-3 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'overview'
                ? 'border-teal-500 text-teal-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('pathways')}
            className={`py-3 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${
              activeTab === 'pathways'
                ? 'border-teal-500 text-teal-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Pathways
            {enrichment && enrichment.pathway_count > 0 && (
              <span className="bg-indigo-100 text-indigo-800 px-2 py-0.5 rounded-full text-xs">
                {enrichment.pathway_count}
              </span>
            )}
            {enrichmentLoading && (
              <span className="animate-spin h-3 w-3 border border-gray-400 border-t-transparent rounded-full"></span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('interactions')}
            className={`py-3 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${
              activeTab === 'interactions'
                ? 'border-teal-500 text-teal-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Interactions
            {enrichment && enrichment.interaction_count > 0 && (
              <span className="bg-cyan-100 text-cyan-800 px-2 py-0.5 rounded-full text-xs">
                {enrichment.interaction_count.toLocaleString()}
              </span>
            )}
            {enrichmentLoading && (
              <span className="animate-spin h-3 w-3 border border-gray-400 border-t-transparent rounded-full"></span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('reactions')}
            className={`py-3 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${
              activeTab === 'reactions'
                ? 'border-teal-500 text-teal-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Reactions
            {reactions.length > 0 && (
              <span className="bg-green-100 text-green-800 px-2 py-0.5 rounded-full text-xs">
                {reactions.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('ligands')}
            className={`py-3 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${
              activeTab === 'ligands'
                ? 'border-teal-500 text-teal-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Ligands
            {ligands.length > 0 && (
              <span className="bg-purple-100 text-purple-800 px-2 py-0.5 rounded-full text-xs">
                {ligands.length}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Sequence card */}
            <div className="bg-white rounded-lg border shadow-sm">
              <div className="px-4 py-3 border-b flex items-center justify-between">
                <h3 className="font-semibold text-gray-900">Sequence</h3>
                <button
                  onClick={() => navigator.clipboard.writeText(protein.sequence)}
                  className="text-sm text-teal-600 hover:text-teal-800"
                >
                  Copy
                </button>
              </div>
              <div className="p-4">
                <div className="font-mono text-xs leading-relaxed break-all max-h-64 overflow-y-auto bg-gray-50 rounded p-3">
                  {protein.sequence}
                </div>
              </div>
            </div>

            {/* 3D Structure card */}
            <div className="bg-white rounded-lg border shadow-sm">
              <div className="px-4 py-3 border-b flex items-center justify-between">
                <h3 className="font-semibold text-gray-900">3D Structure</h3>
                {(protein.uniprot_id || protein.properties?.pdb_content) && (
                  <button
                    onClick={() => setStructureViewerOpen(true)}
                    className="text-sm text-purple-600 hover:text-purple-800 flex items-center gap-1"
                  >
                    Open in Mol*
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </button>
                )}
              </div>
              <div className="p-4">
                {protein.uniprot_id ? (
                  <div className="text-center py-8">
                    <div className="text-purple-500 mb-3">
                      <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                      </svg>
                    </div>
                    <p className="text-sm text-gray-600 mb-3">
                      AlphaFold structure available for {protein.uniprot_id}
                    </p>
                    <button
                      onClick={() => setStructureViewerOpen(true)}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                    >
                      View 3D Structure
                    </button>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-400">
                    <svg className="w-16 h-16 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <p className="text-sm">No UniProt ID - structure unavailable</p>
                  </div>
                )}
              </div>
            </div>

            {/* Pathways Summary card */}
            <div className="bg-white rounded-lg border shadow-sm">
              <div className="px-4 py-3 border-b flex items-center justify-between">
                <h3 className="font-semibold text-gray-900">Pathways</h3>
                {enrichment && enrichment.pathway_count > 0 && (
                  <span className="text-sm text-gray-500">{enrichment.pathway_count} total</span>
                )}
              </div>
              <div className="p-4">
                {!protein.uniprot_id ? (
                  <p className="text-sm text-gray-400">No UniProt ID - pathway data unavailable</p>
                ) : enrichmentLoading ? (
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <span className="animate-spin h-4 w-4 border-2 border-teal-500 border-t-transparent rounded-full"></span>
                    Loading pathways...
                  </div>
                ) : enrichment && enrichment.top_pathways.length > 0 ? (
                  <div className="space-y-2">
                    {enrichment.top_pathways.slice(0, 5).map((pathway) => (
                      <a
                        key={pathway.id}
                        href={`https://reactome.org/content/detail/${pathway.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block text-sm text-indigo-600 hover:text-indigo-800 hover:underline truncate"
                        title={pathway.name}
                      >
                        {pathway.name}
                      </a>
                    ))}
                    {enrichment.pathway_count > 5 && (
                      <button
                        onClick={() => setActiveTab('pathways')}
                        className="text-sm text-teal-600 hover:text-teal-800"
                      >
                        View all {enrichment.pathway_count} pathways
                      </button>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No pathway data found</p>
                )}
              </div>
            </div>

            {/* Interactions Summary card */}
            <div className="bg-white rounded-lg border shadow-sm">
              <div className="px-4 py-3 border-b flex items-center justify-between">
                <h3 className="font-semibold text-gray-900">Protein Interactions</h3>
                {enrichment && enrichment.interaction_count > 0 && (
                  <span className="text-sm text-gray-500">{enrichment.interaction_count.toLocaleString()} total</span>
                )}
              </div>
              <div className="p-4">
                {!protein.uniprot_id ? (
                  <p className="text-sm text-gray-400">No UniProt ID - interaction data unavailable</p>
                ) : enrichmentLoading ? (
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <span className="animate-spin h-4 w-4 border-2 border-teal-500 border-t-transparent rounded-full"></span>
                    Loading interactions...
                  </div>
                ) : enrichment && enrichment.top_interactors.length > 0 ? (
                  <div className="space-y-2">
                    {enrichment.top_interactors.map((interactor, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <span className="font-medium text-sm w-20 truncate" title={interactor.name}>
                          {interactor.name}
                        </span>
                        <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full ${getScoreColor(interactor.score)} transition-all`}
                            style={{ width: `${(interactor.score / 999) * 100}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-500 w-12 text-right">{interactor.score}</span>
                      </div>
                    ))}
                    {enrichment.interaction_count > 5 && (
                      <button
                        onClick={() => setActiveTab('interactions')}
                        className="text-sm text-teal-600 hover:text-teal-800"
                      >
                        View all interactions
                      </button>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No interaction data found</p>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'pathways' && (
          <PathwaysTab uniprotId={protein.uniprot_id} />
        )}

        {activeTab === 'interactions' && (
          <InteractionsTab uniprotId={protein.uniprot_id} />
        )}

        {activeTab === 'reactions' && (
          <div className="bg-white rounded-lg border shadow-sm">
            <div className="px-4 py-3 border-b">
              <h3 className="font-semibold text-gray-900">
                Linked Reactions ({reactions.length})
              </h3>
              <p className="text-sm text-gray-500">
                Reactions where this protein acts as a catalyst, substrate, or regulator
              </p>
            </div>
            <div className="divide-y">
              {reactions.length > 0 ? (
                reactions.map((rxn) => (
                  <div key={rxn.id} className="p-4 hover:bg-gray-50">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium">
                            {rxn.role}
                          </span>
                          <span className="text-sm text-gray-500">
                            Reaction #{rxn.reaction_id}
                          </span>
                        </div>
                        <p className="font-mono text-sm text-gray-700 break-all">
                          {rxn.reaction_smiles || 'No SMILES available'}
                        </p>
                        {rxn.file_name && (
                          <p className="text-xs text-gray-500 mt-1">
                            From: {rxn.file_name}
                          </p>
                        )}
                      </div>
                      <Link
                        to={`/chemistry/file/${rxn.file_id}/editor`}
                        className="text-sm text-teal-600 hover:text-teal-800"
                      >
                        View file
                      </Link>
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-8 text-center text-gray-500">
                  No reactions linked to this protein
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'ligands' && (
          <div className="bg-white rounded-lg border shadow-sm">
            <div className="px-4 py-3 border-b">
              <h3 className="font-semibold text-gray-900">
                Linked Ligands ({ligands.length})
              </h3>
              <p className="text-sm text-gray-500">
                Linked compounds and their recorded relationship to this protein
              </p>
            </div>
            <div className="divide-y">
              {ligands.length > 0 ? (
                ligands.map((lig) => (
                  <ProteinLigandCard key={lig.id} ligand={lig} />
                ))
              ) : (
                <div className="p-8 text-center text-gray-500">
                  No ligands linked to this protein
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Structure Viewer Modal */}
      <StructureViewer
        isOpen={structureViewerOpen}
        onClose={() => setStructureViewerOpen(false)}
        pdbContent={protein.properties?.pdb_content}
        uniprotId={protein.uniprot_id || undefined}
        title={protein.name || `Protein #${protein.id}`}
      />
    </div>
  );
};

// Pathways Tab Component
const PathwaysTab: React.FC<{ uniprotId: string | null }> = ({ uniprotId }) => {
  const [pathways, setPathways] = useState<Array<{pathway_id: string; pathway_name: string; evidence_code: string}>>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!uniprotId) return;

    setLoading(true);
    fetch(`/api/enrich/pathways/${uniprotId}`, { headers: getAuthHeaders() })
      .then(res => {
        if (!res.ok) throw new Error(`Pathway request failed (${res.status})`);
        return res.json();
      })
      .then(data => {
        setPathways(data.pathways || []);
      })
      .catch(err => console.error('Failed to load pathways:', err))
      .finally(() => setLoading(false));
  }, [uniprotId]);

  if (!uniprotId) {
    return (
      <div className="bg-white rounded-lg border shadow-sm p-8 text-center text-gray-500">
        <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
        <p className="text-lg font-medium mb-2">No UniProt ID</p>
        <p className="text-sm">Pathway data requires a UniProt identifier</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg border shadow-sm p-8 text-center">
        <div className="animate-spin h-8 w-8 border-2 border-teal-500 border-t-transparent rounded-full mx-auto mb-4"></div>
        <p className="text-gray-500">Loading pathways from Reactome...</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border shadow-sm">
      <div className="px-4 py-3 border-b">
        <h3 className="font-semibold text-gray-900">
          Reactome Pathways ({pathways.length})
        </h3>
        <p className="text-sm text-gray-500">
          Biological pathways involving this protein
        </p>
      </div>
      <div className="divide-y max-h-[600px] overflow-y-auto">
        {pathways.length > 0 ? (
          pathways.map((pathway, i) => (
            <div key={i} className="p-4 hover:bg-gray-50 flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <a
                  href={`https://reactome.org/content/detail/${pathway.pathway_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-600 hover:text-indigo-800 hover:underline font-medium"
                >
                  {pathway.pathway_name}
                </a>
                <p className="text-xs text-gray-500 mt-1">
                  {pathway.pathway_id}
                  {pathway.evidence_code && (
                    <span className="ml-2 px-1.5 py-0.5 bg-gray-100 rounded text-gray-600">
                      {pathway.evidence_code}
                    </span>
                  )}
                </p>
              </div>
              <a
                href={`https://reactome.org/content/detail/${pathway.pathway_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-gray-600 ml-4"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>
          ))
        ) : (
          <div className="p-8 text-center text-gray-500">
            No pathways found for this protein
          </div>
        )}
      </div>
    </div>
  );
};

// Interactions Tab Component
const InteractionsTab: React.FC<{ uniprotId: string | null }> = ({ uniprotId }) => {
  const [interactions, setInteractions] = useState<Array<{protein2_name: string; combined_score: number; confidence: string}>>([]);
  const [loading, setLoading] = useState(false);
  const [minScore, setMinScore] = useState(700);

  useEffect(() => {
    if (!uniprotId) return;

    setLoading(true);
    fetch(`/api/enrich/interactions/${uniprotId}?min_score=${minScore}&limit=100`, {
      headers: getAuthHeaders(),
    })
      .then(res => {
        if (!res.ok) throw new Error(`Interaction request failed (${res.status})`);
        return res.json();
      })
      .then(data => {
        setInteractions(data.interactions || []);
      })
      .catch(err => console.error('Failed to load interactions:', err))
      .finally(() => setLoading(false));
  }, [uniprotId, minScore]);

  const getScoreColor = (score: number) => {
    if (score >= 900) return 'bg-green-500';
    if (score >= 700) return 'bg-blue-500';
    if (score >= 400) return 'bg-yellow-500';
    return 'bg-gray-400';
  };

  const getConfidenceBadge = (confidence: string) => {
    const colors: Record<string, string> = {
      highest: 'bg-green-100 text-green-800',
      high: 'bg-blue-100 text-blue-800',
      medium: 'bg-yellow-100 text-yellow-800',
      low: 'bg-gray-100 text-gray-800',
    };
    return colors[confidence] || 'bg-gray-100 text-gray-800';
  };

  if (!uniprotId) {
    return (
      <div className="bg-white rounded-lg border shadow-sm p-8 text-center text-gray-500">
        <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
        <p className="text-lg font-medium mb-2">No UniProt ID</p>
        <p className="text-sm">Interaction data requires a UniProt identifier</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border shadow-sm">
      <div className="px-4 py-3 border-b flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-gray-900">
            STRING Protein Interactions ({interactions.length})
          </h3>
          <p className="text-sm text-gray-500">
            Protein-protein interactions from STRING database
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600">Min Score:</label>
          <select
            value={minScore}
            onChange={(e) => setMinScore(parseInt(e.target.value))}
            className="border rounded px-2 py-1 text-sm"
          >
            <option value={900}>900+ (Highest)</option>
            <option value={700}>700+ (High)</option>
            <option value={400}>400+ (Medium)</option>
            <option value={150}>150+ (Low)</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="p-8 text-center">
          <div className="animate-spin h-8 w-8 border-2 border-teal-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-500">Loading interactions from STRING...</p>
        </div>
      ) : (
        <div className="divide-y max-h-[600px] overflow-y-auto">
          {interactions.length > 0 ? (
            interactions.map((interaction, i) => (
              <div key={i} className="p-4 hover:bg-gray-50 flex items-center gap-4">
                <div className="w-24 font-medium text-gray-900 truncate" title={interaction.protein2_name}>
                  {interaction.protein2_name}
                </div>
                <div className="flex-1">
                  <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${getScoreColor(interaction.combined_score)} transition-all`}
                      style={{ width: `${(interaction.combined_score / 999) * 100}%` }}
                    />
                  </div>
                </div>
                <div className="w-12 text-right text-sm text-gray-600">
                  {interaction.combined_score}
                </div>
                <span className={`px-2 py-1 rounded text-xs font-medium ${getConfidenceBadge(interaction.confidence)}`}>
                  {interaction.confidence}
                </span>
              </div>
            ))
          ) : (
            <div className="p-8 text-center text-gray-500">
              No interactions found with score ≥ {minScore}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProteinDetailPage;
